import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, Users, ShieldCheck, Building2, Sparkles, Warehouse, TicketCheck, Landmark, Building, HeartPulse, GraduationCap, Briefcase, Hotel, ShoppingBag, Factory, HardHat, Home, CalendarDays, Store, Cog, BadgeCheck, ClipboardCheck, Handshake, FileCheck } from "lucide-react";
import { COMPANY } from "@/data/site";

export const ICONS = {
  Users, ShieldCheck, Building2, Sparkles, Warehouse, TicketCheck, Landmark, Building, HeartPulse,
  GraduationCap, Briefcase, Hotel, ShoppingBag, Factory, HardHat, Home, CalendarDays, Store, Cog,
  BadgeCheck, ClipboardCheck, Handshake, FileCheck,
};

export const Reveal = ({ children, delay = 0, y = 28, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const Eyebrow = ({ children, light = false }) => (
  <span className={`inline-flex items-center gap-2 text-xs font-semibold tracking-[0.22em] uppercase ${light ? "text-gold" : "text-gold-hover"}`}>
    <span className="w-8 h-px bg-gold inline-block" />
    {children}
  </span>
);

export const SectionHeading = ({ eyebrow, title, desc, center = false, light = false }) => (
  <Reveal className={`max-w-3xl ${center ? "mx-auto text-center" : ""} mb-12 sm:mb-16`}>
    {center ? (
      <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.22em] uppercase text-gold-hover mb-4">
        <span className="w-8 h-px bg-gold inline-block" />{eyebrow}<span className="w-8 h-px bg-gold inline-block" />
      </span>
    ) : (
      <div className="mb-4"><Eyebrow light={light}>{eyebrow}</Eyebrow></div>
    )}
    <h2 className={`font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight ${light ? "text-white" : "text-navy"}`}>
      {title}
    </h2>
    {desc && <p className={`mt-4 text-base sm:text-lg leading-relaxed ${light ? "text-slate-300" : "text-slate-600"}`}>{desc}</p>}
  </Reveal>
);

export const PageHero = ({ title, subtitle, crumb }) => (
  <section className="relative bg-navy overflow-hidden hero-grain" data-testid="page-hero">
    <div className="absolute -right-24 -top-24 w-[28rem] h-[28rem] rounded-full bg-gold/10 blur-3xl pointer-events-none" />
    <div className="absolute -left-32 -bottom-40 w-[26rem] h-[26rem] rounded-full bg-navy-light blur-3xl pointer-events-none" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 lg:py-28 relative">
      <motion.p
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-xs font-semibold tracking-[0.22em] uppercase text-gold mb-5"
      >
        {crumb}
      </motion.p>
      <span className="block overflow-hidden">
        <motion.h1
          initial={{ y: "110%" }}
          animate={{ y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05]"
        >
          {title}
        </motion.h1>
      </span>
      {subtitle && (
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.45 }}
          className="mt-6 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed"
        >
          {subtitle}
        </motion.p>
      )}
      <div className="gold-line-grow h-0.5 w-28 bg-gold mt-9 scale-x-0" />
    </div>
  </section>
);

export const CTABand = ({ title = "Discuss your manpower requirement with our team.", desc = "Share your requirement and we will respond with a structured proposal and quotation." }) => (
  <section className="relative bg-navy overflow-hidden hero-grain" data-testid="cta-band">
    <div className="absolute right-0 top-0 w-80 h-80 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
        <Reveal className="max-w-2xl">
          <Eyebrow light>Get Started</Eyebrow>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mt-4 leading-tight">{title}</h2>
          <p className="mt-3 text-slate-300 text-base sm:text-lg">{desc}</p>
        </Reveal>
        <Reveal delay={0.15} className="flex flex-col sm:flex-row gap-4 shrink-0">
          <Link
            to="/request-quote"
            data-testid="cta-quote-btn"
            className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-hover text-navy font-heading font-bold text-xs tracking-widest uppercase px-7 py-4 transition-all hover:gap-3"
          >
            Request a Quote <ArrowRight size={15} />
          </Link>
          <a
            href={COMPANY.phoneHref}
            data-testid="cta-call-btn"
            className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-gold text-white hover:text-gold font-heading font-bold text-xs tracking-widest uppercase px-7 py-4 transition-colors"
          >
            <Phone size={15} /> {COMPANY.phone}
          </a>
        </Reveal>
      </div>
    </div>
  </section>
);

export const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);
